import React from 'react';
import { RankingComponentProps } from 'types/ranking';
import { getAvatarImage } from 'utils/avatars';
import { Trophy, TrendingUp, Award, Star } from 'lucide-react';
import './styles.scss';

export function PrivateRanking({ 
  players, 
  currentUserId,
  title = "Meu Ranking", 
  className = "" 
}: RankingComponentProps) {
  // Encontra o jogador atual
  const currentPlayer = players.find(player => player.id === currentUserId);

  if (!currentPlayer) {
    return (
      <div className={`private-ranking ${className}`}>
        {title && <h2 className="ranking-title">{title}</h2>}
        <div className="no-data-message">
          Você não está participando deste ranking
        </div>
      </div>
    );
  }

  const avatarImage = getAvatarImage(currentPlayer.fotoDePerfil || currentPlayer.avatar);
  
  // Define a cor baseada na posição
  const getPositionColor = () => {
    if (currentPlayer.position === 1) return '#FFD700'; // Ouro
    if (currentPlayer.position === 2) return '#C0C0C0'; // Prata
    if (currentPlayer.position === 3) return '#CD7F32'; // Bronze
    return '#a78bfa'; // Roxo padrão
  };

  // Calcula o progresso em relação ao máximo de pontos
  const progressPercentage = currentPlayer.maxPoints 
    ? Math.min((currentPlayer.points / currentPlayer.maxPoints) * 100, 100)
    : (currentPlayer.progress || 0);

  return (
    <div className={`private-ranking ${className}`}>
      {title && <h2 className="ranking-title">{title}</h2>}

      <div className="private-ranking-content">
        {/* Card principal com avatar e posição */}
        <div className="player-main-card">
          <div className="player-avatar-section">
            <div className="avatar-container">
              {avatarImage ? (
                <img 
                  src={avatarImage} 
                  alt={currentPlayer.name} 
                  className="player-avatar" 
                />
              ) : (
                <div className="player-avatar-placeholder">
                  {currentPlayer.name.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            
            <div className="player-info">
              <h3 className="player-name">{currentPlayer.name}</h3>
              {currentPlayer.teamName && (
                <p className="player-team">
                  {currentPlayer.teamName}
                  {currentPlayer.location && ` • ${currentPlayer.location}`}
                </p>
              )}
            </div>
          </div>

          <div className="position-badge-section">
            <Trophy 
              className="trophy-icon" 
              size={32} 
              strokeWidth={2.5}
              style={{ color: getPositionColor() }}
            />
            <div className="position-display" style={{ color: getPositionColor() }}>
              <span className="position-number">{currentPlayer.position}º</span>
              <span className="position-label">Lugar</span>
            </div>
          </div>
        </div>

        {/* Estatísticas em cards */}
        <div className="stats-grid">
          {/* Pontos */}
          <div className="stat-card">
            <div className="stat-icon-wrapper" style={{ backgroundColor: 'rgba(167, 139, 250, 0.1)' }}>
              <Star size={24} style={{ color: '#a78bfa' }} />
            </div>
            <div className="stat-content">
              <span className="stat-label">Pontos</span>
              <span className="stat-value">{currentPlayer.points.toLocaleString('pt-BR')}</span>
              {currentPlayer.maxPoints && (
                <span className="stat-max">de {currentPlayer.maxPoints.toLocaleString('pt-BR')}</span>
              )}
            </div>
          </div>

          {/* Nível */}
          {currentPlayer.level && (
            <div className="stat-card">
              <div className="stat-icon-wrapper" style={{ backgroundColor: 'rgba(96, 165, 250, 0.1)' }}>
                <TrendingUp size={24} style={{ color: '#60a5fa' }} />
              </div>
              <div className="stat-content">
                <span className="stat-label">Nível</span>
                <span className="stat-value">{currentPlayer.level}</span>
              </div>
            </div>
          )}

          {/* Badges */}
          {currentPlayer.badges !== undefined && (
            <div className="stat-card">
              <div className="stat-icon-wrapper" style={{ backgroundColor: 'rgba(251, 191, 36, 0.1)' }}>
                <Award size={24} style={{ color: '#fbbf24' }} />
              </div>
              <div className="stat-content">
                <span className="stat-label">Conquistas</span>
                <span className="stat-value">{currentPlayer.badges}</span>
              </div>
            </div>
          )}
        </div>

        {/* Barra de progresso */}
        {(currentPlayer.maxPoints || currentPlayer.progress !== undefined) && (
          <div className="progress-section">
            <div className="progress-header">
              <span className="progress-label">Progresso</span>
              <span className="progress-percentage">{Math.round(progressPercentage)}%</span>
            </div>
            <div className="progress-bar-container">
              <div 
                className="progress-bar-fill" 
                style={{ 
                  width: `${progressPercentage}%`,
                  backgroundColor: getPositionColor()
                }}
              />
            </div>
          </div>
        )}

        {/* Informação adicional */}
        <div className="info-footer">
          <p className="info-text">
            🔒 Este é um ranking privado. Apenas você pode ver sua posição e pontuação.
          </p>
        </div>
      </div>
    </div>
  );
}
